import React, { useState } from 'react';
import styles from './ExecutionLogCard.module.css';

interface FileChange {
  path: string;
  type: 'A' | 'M' | 'D' | string;
}

interface ProgressEvent {
  type?: string;
  status?: string;
  tool?: string;
  stage?: string;
  filePath?: string;
  changes?: FileChange[];
  changed?: { cloud?: string[]; public?: string[]; data?: string[] };
}

const ACTION_LABELS: Record<string, string> = {
  A: 'Created',
  M: 'Modified',
  D: 'Deleted',
};

const ACTION_COLORS: Record<string, { dot: string }> = {
  A: { dot: '#22c55e' },
  M: { dot: '#eab308' },
  D: { dot: '#ef4444' },
};

/**
 * Extracts file changes from progress events.
 * Handles both V3 deploy format (changes array) and V2 format (changed object).
 */
function extractChanges(events: ProgressEvent[]): FileChange[] {
  const changes: FileChange[] = [];
  const seen = new Set<string>();

  for (const evt of events) {
    // V3: explicit changes array with type
    if (evt.changes && Array.isArray(evt.changes)) {
      for (const c of evt.changes) {
        if (c.path && !seen.has(c.path)) {
          seen.add(c.path);
          changes.push(c);
        }
      }
    }

    // V2/fallback: changed object without type (assume Modified)
    if (evt.changed && !evt.changes) {
      const allPaths = [
        ...(evt.changed.cloud || []),
        ...(evt.changed.public || []),
        ...(evt.changed.data || []),
      ];
      for (const p of allPaths) {
        if (p && !seen.has(p)) {
          seen.add(p);
          changes.push({ path: p, type: 'M' });
        }
      }
    }
  }

  return changes;
}

function groupByAction(changes: FileChange[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const c of changes) {
    const action = c.type || 'M';
    if (!groups[action]) groups[action] = [];
    groups[action].push(c.path);
  }
  return groups;
}

const LogIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 8h8M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * ExecutionLogCard - Shows what files were changed after execution.
 * Simple flat list grouped by action (Created, Modified, Deleted).
 */
export default function ExecutionLogCard({ events }: { events: ProgressEvent[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const changes = React.useMemo(() => extractChanges(events), [events]);
  const groups = React.useMemo(() => groupByAction(changes), [changes]);
  const actionKeys = Object.keys(groups).sort(); // A, D, M

  if (changes.length === 0) return null;

  return (
    <div className={styles.card}>
      {/* Header */}
      <div
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <LogIcon className={styles.headerIcon} />
        <span className={styles.headerTitle}>Changes made</span>
        <span className={styles.headerCount}>
          {changes.length} {changes.length === 1 ? 'file' : 'files'}
        </span>
        <svg
          className={styles.chevron}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Collapsed: one-line summary */}
      {!isExpanded && (
        <div className={styles.collapsedBody}>
          <div className={styles.collapsedRow}>
            <span className={styles.collapsedName}>
              {actionKeys.map((k) => `${ACTION_LABELS[k] || k} ${groups[k].length}`).join(', ')}
            </span>
          </div>
        </div>
      )}

      {/* Expanded: files grouped by action */}
      {isExpanded && (
        <div className={styles.body}>
          {actionKeys.map((action) => {
            const color = ACTION_COLORS[action] || { dot: '#9ca3af' };
            const label = ACTION_LABELS[action] || action;
            const files = groups[action];
            return (
              <div key={action} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: color.dot, marginBottom: '4px' }}>
                  {label}
                </div>
                {files.map((f) => (
                  <div
                    key={f}
                    style={{
                      fontSize: '13px',
                      color: '#9ca3af',
                      fontFamily: 'monospace',
                      padding: '2px 0 2px 12px',
                    }}
                  >
                    {f}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
